import { useState, useEffect, createContext, useContext } from 'react'
import { Outlet, useParams } from 'react-router-dom'
import { BackToTop, useIsMobile } from 'wss3-forge'
import { DocsHeader } from './DocsHeader'
import { DocsFooter } from './DocsFooter'
import { DocsSidebar } from './DocsSidebar'
import { ScrollProgress } from './ScrollProgress'
import { DocStructure, getDocStructure } from '../lib/docs'

interface StructureContextValue {
  structure: DocStructure | null
  loading: boolean
}

const StructureContext = createContext<StructureContextValue>({
  structure: null,
  loading: true
})

export function useDocStructure() {
  return useContext(StructureContext)
}

export function DocsLayout() {
  const { pluginSlug } = useParams<{ pluginSlug?: string }>()
  const isMobile = useIsMobile()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [structure, setStructure] = useState<DocStructure | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!pluginSlug) {
      setStructure(null)
      setLoading(false)
      return
    }

    setLoading(true)
    getDocStructure(pluginSlug)
      .then(setStructure)
      .catch(() => setStructure(null))
      .finally(() => setLoading(false))
  }, [pluginSlug])

  useEffect(() => {
    setSidebarOpen(false)
  }, [pluginSlug])

  const showSidebar = !!pluginSlug && !!structure
  const isHome = !pluginSlug

  return (
    <StructureContext.Provider value={{ structure, loading }}>
      <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-primary)' }}>
        <DocsHeader
          onMenuClick={() => setSidebarOpen(true)}
          showMenuButton={isMobile && showSidebar}
          transparentOnTop={isHome}
        />
        {!isHome && <ScrollProgress />}

        {/* Fixed Sidebar - Desktop */}
        {showSidebar && !isMobile && (
          <div
            style={{
              position: 'fixed',
              top: 77,
              left: 0,
              width: 280,
              height: 'calc(100vh - 77px)',
              zIndex: 50,
              overflow: 'hidden'
            }}
          >
            <DocsSidebar structure={structure} mode="inline" />
          </div>
        )}

        {/* Mobile Sidebar (drawer) */}
        {showSidebar && isMobile && (
          <DocsSidebar
            structure={structure}
            mode="drawer"
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main
          style={{
            marginLeft: showSidebar && !isMobile ? 280 : 0,
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div style={{ paddingTop: isHome ? 0 : 77, flex: 1 }}>
            <Outlet />
          </div>
          <DocsFooter />
        </main>

        <BackToTop threshold={300} />
      </div>
    </StructureContext.Provider>
  )
}
